import React from 'react';
import { formatDateDMY } from '../../../utils/formatDate';
import ActionButtons from './ActionButton';
import {formatAmount} from '../../../utils/formatAmount';

const GenericCard = ({
  item,
  onEdit,
  onDelete,
  onViewDetails,
  baseCurrency,
  cardType = 'saving' // 'saving', 'debt', 'mortgage'
}) => {
  const formatCurrency = (amount) => {
    return `${baseCurrency} ${formatAmount(amount)}`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-700 border-green-200';
      case 'active': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'archived': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'paused': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'overdue': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getProgressPercentage = () => {
    switch (cardType) {
      case 'saving':
        return item.targetAmount > 0
          ? Math.min((item.currentAmount / item.targetAmount) * 100, 100)
          : 0;
      case 'debt':
      case 'mortgage':
        return item.initialAmount > 0
          ? Math.min(((item.amountRepaid || 0) / item.initialAmount) * 100, 100)
          : 0;
      default:
        return 0;
    }
  };


  const getCardFields = () => {
    switch (cardType) {
      case 'saving':
        return [
          { label: 'Amount Saved', value: formatCurrency(item.currentAmount) },
          { label: 'Target Amount', value: formatCurrency(item.targetAmount) },
          { label: 'Target Date', value: formatDateDMY(item?.targetDate) },
        ];
      case 'debt':
        return [
          { label: 'Initial Amount', value: formatCurrency(item.initialAmount) },
          { label: 'Amount Repaid', value: formatCurrency(item.amountRepaid) },
          { label: 'Balance', value: formatCurrency(item.balance || (item.initialAmount - (item.amountRepaid || 0))) },
          { label: 'Est. Final Payment', value: formatDateDMY(item.estimatedFinalPaymentDate) },
        ];
      case 'mortgage':
        return [
          { label: 'Initial Amount', value: formatCurrency(item.initialAmount) },
          { label: 'Amount Repaid', value: formatCurrency(item.amountRepaid) },
          { label: 'Balance', value: formatCurrency(item.balance || (item.initialAmount - (item.amountRepaid || 0))) },
          { label: 'Monthly Payment', value: formatCurrency(item.monthlyPayment) },
        ];
      default:
        return [];
    }
  };

  const progressPercentage = getProgressPercentage();
  const cardFields = getCardFields();

  const getAutoStatus = () => {
    if (cardType === 'saving') {
      if (progressPercentage >= 100|| item.status==='completed') {
        return 'completed';
      } else if (item.status === 'archived') {
        return 'archived';
      } else {
        return 'active';
      }
    }
    return item.status || 'active';
  };

  const currentStatus = getAutoStatus();

  return (
    <div
      className="bg-white rounded-2xl p-5 shadow-md border border-gray-100 hover:shadow-lg transition-shadow cursor-pointer group relative"
      onClick={onViewDetails}
      style={{ minWidth: 0 }}
    >
      {/* Title & Actions */}
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-base md:text-lg font-semibold text-gray-900 truncate">
          {item.title || item.name}
        </h3>
        <div onClick={(e) => e.stopPropagation()} className="ml-2">
          <ActionButtons
            onEdit={() => onEdit()}
            onDelete={() => onDelete()}
          />
        </div>
      </div>

      {/* Card Fields */}
      <div className="space-y-2 mb-3">
        {cardFields.map((field, index) => (
          <div key={index} className="flex justify-between items-center text-sm">
            <span className="text-gray-500">{field.label}</span>
            <span className="font-medium text-gray-900">{field.value}</span>
          </div>
        ))}
      </div>

      {/* Progress Bar */}
      <div className="mt-2 mb-2">
        <div className="flex justify-between items-center text-xs mb-1">
          <span className="text-gray-500">
            {cardType === 'saving' ? 'Progress' : 'Repaid'}
          </span>
          <span className="font-semibold text-gray-700">{progressPercentage.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className={`bg-btn-primary h-2.5 rounded-full transition-all duration-300`}
            style={{ width: `${Math.min(progressPercentage, 100)}%` }}
          ></div>
        </div>
      </div>

      {/* Status Badge */}
      <div className="flex justify-end mt-3">
        <span
          className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(currentStatus)} shadow-sm`}
        >
          {currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)}
        </span>
      </div>
    </div>
  );
};

export default GenericCard;